import { perColumnSearchConfig } from '../manager/pledges/config';
import { getSelectedPerColumnSearchColumns } from '../components/per-column-search';
import DataGrid from '../manager/data-grid/index.js';

export const SORT_KEY_TO_CELL = {
    type: 'type',
    display_id: 'display_id',
    name: 'name',
    date_pledged: 'date_pledged',
    added_by: 'added_by',
    pledge_amount: 'pledge_amount',
    amount_paid: 'amount_paid',
    amount_refunded: 'amount_refunded',
    amount_due: 'amount_due',
    campaign: 'campaign',
    description: 'description',
    status: 'status'
};

const STATUS_SORT_ORDER = {
    'Unpaid': 1,
    'Partially Paid': 2,
    'In-Progress': 3,
    'Failed': 4,
    'Cancelled': 5,
    'Refunded': 6,
    'Paid': 7,
    'Completed': 8
};

export function getStatusFromRow(row) {
    const paid = row.dataset.paid === '1';
    const recurring = row.dataset.recurring === '1';
    const activeOngoing = row.dataset.activeOngoing === '1';
    const deleted = row.dataset.deleted === '1';
    const cancelled = row.dataset.cancelled === '1';
    const failed = row.dataset.failed === '1';
    const balanceDue = parseFloat(row.dataset.balanceDue || '0');
    const amount = parseFloat(row.dataset.amount || '0');
    if (recurring) {
        if (cancelled) return 'Cancelled';
        if (failed) return 'Failed';
        if (activeOngoing && !paid && !deleted) return 'In-Progress';
    }
    const refundStatus = getRefundStatus(row, amount);
    if (refundStatus) return refundStatus;
    if (recurring) return 'Completed';
    if (paid || deleted) return 'Paid';
    return balanceDue === amount ? 'Unpaid' : 'Partially Paid';
}

function getRefundStatus(row, amount) {
    const refunded = parseFloat(row.dataset.amountRefunded || '0');
    if (refunded <= 0.009) return null;
    const netPaid = parseFloat(row.dataset.amountPaid || '0');
    if (netPaid <= 0.009) return 'Refunded';
    return netPaid < amount - 0.009 ? 'Partially Paid' : null;
}

export function getRecurringStatusFromRow(row) {
    return getStatusFromRow(row);
}

export function getTypeFromRow(row) {
    return (row.dataset.pledgeType || '').trim();
}

export function getSearchColumnKeys() {
    return Array.isArray(perColumnSearchConfig?.columnKeys) ? perColumnSearchConfig.columnKeys : [];
}

function readCellText(row, cellClass) {
    const cell = row.querySelector('.' + cellClass);
    return cell?.textContent?.trim() ?? '';
}

function normalizeSearchText(value) {
    return String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

function readAmountColumnSearchValue(row, cellClass) {
    const cell = row.querySelector('.' + cellClass);
    if (!cell) {
        return '';
    }
    return (cell.textContent.trim() + ' ' + (cell.dataset.sortValue || '')).toLowerCase();
}

function readAmountSortValue(row, key) {
    const cell = row.querySelector('.data-grid-cell-' + SORT_KEY_TO_CELL[key]);
    const parsed = parseFloat(cell?.dataset?.sortValue ?? '');
    return Number.isFinite(parsed) ? parsed : 0;
}

function readFullNameSearchValue(row) {
    return normalizeSearchText(row.getAttribute('data-user-name'));
}

export function getValueForSearchColumn(row, colKey) {
    switch (colKey) {
        case 'type':         return (row.getAttribute('data-pledge-type') || '').toLowerCase();
        case 'display_id':   return readCellText(row, 'data-grid-cell-display_id').toLowerCase();
        case 'name':
        case 'full_name':    return readFullNameSearchValue(row);
        case 'date_pledged': return (row.getAttribute('data-pledge-date') || '').toLowerCase();
        case 'added_by':     return (row.getAttribute('data-added-by') || '').toLowerCase();
        case 'pledge_amount': return readAmountColumnSearchValue(row, 'data-grid-cell-pledge_amount');
        case 'amount_paid': return readAmountColumnSearchValue(row, 'data-grid-cell-amount_paid');
        case 'amount_refunded': return readAmountColumnSearchValue(row, 'data-grid-cell-amount_refunded');
        case 'amount_due':    return readAmountColumnSearchValue(row, 'data-grid-cell-amount_due');
        case 'campaign':     return (row.getAttribute('data-campaign') || '').toLowerCase();
        case 'description':  return (row.getAttribute('data-description') || '').toLowerCase();
        case 'status':       return getStatusFromRow(row).toLowerCase();
        default: return '';
    }
}

function getSelectedSearchColumns() {
    const cols = getSelectedPerColumnSearchColumns(perColumnSearchConfig);
    if (cols.length > 0) {
        return cols;
    }
    return getSearchColumnKeys();
}

function resolveSearchColumns(columns) {
    if (!columns.includes('first_name') || !columns.includes('last_name')) {
        return columns;
    }

    return columns
        .filter(colKey => colKey !== 'first_name' && colKey !== 'last_name')
        .concat('full_name');
}

export function createSearchMatcher(query) {
    const trimmed = normalizeSearchText(query);
    if (!trimmed) return () => true;
    const columns = resolveSearchColumns(getSelectedSearchColumns());
    return (row) => columns.some(colKey => {
        const val = getValueForSearchColumn(row, colKey);
        return val && val.includes(trimmed);
    });
}

export function getSortValue(row, key) {
    if (key === 'date_pledged') {
        const dayCell = row.querySelector('.data-grid-cell-date_pledged');
        const dayVal = Number(dayCell?.dataset?.sortValue);
        const sortAt = Number(row.getAttribute('data-sort-at'));
        const safeDay = Number.isFinite(dayVal) ? dayVal : 0;
        const safeSortAt = Number.isFinite(sortAt) ? sortAt : 0;
        const dayOffset = safeSortAt >= safeDay ? safeSortAt - safeDay : safeSortAt;
        return safeDay * 1000000 + dayOffset;
    }
    if (key === 'pledge_amount' || key === 'amount_paid' || key === 'amount_refunded' || key === 'amount_due') {
        return readAmountSortValue(row, key);
    }
    if (key === 'status') {
        return STATUS_SORT_ORDER[getStatusFromRow(row)] ?? 0;
    }
    return DataGrid.getSortValueFromRow(row, key, SORT_KEY_TO_CELL, { getStatusFromRow, getTypeFromRow });
}
